/* EVO Reveal kit: hero intro video, scroll scenes, reveals, counters, highlight gallery, launch bar and the
   "Erinnere mich" sheet with the Klaviyo signup. Used by sections/evo-rv-*.liquid (styles: assets/evo-rv.css).

   Hooks (all optional, any section can use them):
     [data-rv-intro]                 hero video that plays once on load and rests on its last frame
     canvas[data-rv-drift]           hero effect: crumbs + cocoa dust that keep floating once the intro settles
                                     (data-sprites = strip of square crumb images; beam: canvas[data-rv-beam])
     [data-rv-scene]                 tall block with a sticky stage → --p (0..1 while pinned)
       [data-rv-range="a,b[,ease]"]  child → --q (p mapped onto a..b, eased) + data-rv-state before|during|after
       [data-rv-steps] > [data-rv-step][data-rv-range]  → .is-active on the step whose range has started last
     [data-rv-view]                  → --v (0 = top enters the viewport, 1 = bottom has left it)
     [data-rv-words]                 words light up while the block passes the middle of the screen
     [data-rv-reveal]                fades/blurs in once (stagger with style="--i:n")
     [data-rv-count="20"]            counts up once (data-rv-from, data-rv-decimals)
     [data-rv-gallery]               autoplay card gallery (cards, dots, play/pause)
     [data-rv-paddles]               swipe row ([data-rv-row]) with prev/next buttons ([data-rv-paddle="-1|1"])
     [data-rv-bg="url(…)"]           sets --rv-img once the element comes near (CSS backgrounds load eagerly)
     [data-rv-countdown="<ms>"]      days/hours/minutes/seconds to a launch time
     [data-rv-float]                 launch bar, shown between the hero and the signup section
     [data-rv-klaviyo]               signup wrapper (forms inside are [data-rv-signup])
     dialog[data-rv-sheet]           opened by every link ending in #erinnere-mich
     [data-rv-live-link] + [data-rv-launch]  at launch time: [data-rv-cta] links → buy link (data-live-label),
                                     [data-live-text] swap their text, [data-rv-prelaunch] hide, [data-rv-postlaunch] show */

const root = document.documentElement;
root.classList.add('evo-rv-on');

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const phone = window.matchMedia('(max-width: 749px)');
const design = Boolean(window.Shopify && window.Shopify.designMode);
const clamp = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const EASE = {
  linear: (t) => t,
  out: (t) => 1 - Math.pow(1 - t, 3),
  in: (t) => t * t * t,
  inout: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
};

/* ---------- one rAF loop for everything scroll-driven ---------- */

const tasks = new Set();
let queued = false;
const run = () => {
  queued = false;
  const vh = window.innerHeight;
  tasks.forEach((task) => task(vh));
};
// scroll work bound to an element; it removes itself once the element is gone (section reload in the editor)
const addTask = (el, fn) => {
  const task = (vh) => (el.isConnected ? fn(vh) : tasks.delete(task));
  tasks.add(task);
};
const request = () => {
  if (!queued) {
    queued = true;
    requestAnimationFrame(run);
  }
};
window.addEventListener('scroll', request, { passive: true });
window.addEventListener('resize', request);
reduce.addEventListener?.('change', request);

/* ---------- scenes ---------- */

function parseRange(node) {
  const [a, b, e] = (node.dataset.rvRange || '0,1').split(',');
  return { a: parseFloat(a) || 0, b: parseFloat(b) || 1, e: EASE[(e || 'linear').trim()] || EASE.linear };
}

function scene(el) {
  if (el._rvScene) return el._rvScene;
  const ranged = [...el.querySelectorAll('[data-rv-range]')].map((n) => ({ n, ...parseRange(n), q: -1, s: '' }));
  const steps = [...el.querySelectorAll('[data-rv-step]')].map((n) => ({ n, ...parseRange(n) }));
  const api = { p: -1 };
  el._rvScene = api;

  addTask(el, (vh) => {
    const r = el.getBoundingClientRect();
    const p = reduce.matches ? 1 : clamp(-r.top / Math.max(1, r.height - vh));
    if (Math.abs(p - api.p) < 0.0001) return;
    api.p = p;
    el.style.setProperty('--p', p.toFixed(4));
    for (const R of ranged) {
      const q = R.e(clamp((p - R.a) / (R.b - R.a || 1)));
      if (q !== R.q) {
        R.q = q;
        R.n.style.setProperty('--q', q.toFixed(4));
      }
      const s = q <= 0 ? 'before' : q >= 1 ? 'after' : 'during';
      if (s !== R.s) {
        R.s = s;
        R.n.dataset.rvState = s;
      }
    }
    if (steps.length) {
      let active = 0;
      steps.forEach((S, i) => {
        if (p >= S.a) active = i;
      });
      steps.forEach((S, i) => S.n.classList.toggle('is-active', i === active));
    }
  });
  request();
  return api;
}

/* ---------- hero intro: plays once on load, then rests on its last frame (Apple) ---------- */

// gives up on the video if it has not started by then, so a slow network never shows the bar late
const INTRO_WAIT = 3500;

function intro(el) {
  const video = el.querySelector('video');
  const set = (state) => (el.dataset.state = state);
  if (!video || reduce.matches || navigator.connection?.saveData) return set('end');
  const d = el.dataset;
  const mobile = phone.matches;
  const h264 = video.canPlayType('video/mp4; codecs="avc1.640028"');
  let started = false;
  let finished = false;
  let watchdog = 0;
  const finish = (state) => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    clearTimeout(watchdog);
    if (state === 'end') {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    set(state);
  };
  const giveUp = () => finish('end');
  const timer = setTimeout(() => started || giveUp(), INTRO_WAIT);
  video.addEventListener('playing', () => {
    if (finished || started) return;
    started = true;
    clearTimeout(timer);
    set('playing');
    // a stream that stalls halfway must still end on the last frame
    watchdog = setTimeout(giveUp, ((video.duration || 4) + 2.5) * 1000);
  });
  video.addEventListener('ended', () => finish('done'), { once: true });
  video.addEventListener('error', giveUp, { once: true });
  // reloaded further down the page: just show the result
  if (el.getBoundingClientRect().bottom < 0) return giveUp();
  video.preload = 'auto';
  video.src = (mobile ? (h264 ? d.mMp4 : d.mWebm) : h264 ? d.dMp4 : d.dWebm) || '';
  // play() right away: iOS only fetches media data once play() is called, waiting for canplay would never end
  const p = video.play();
  if (p && p.catch) p.catch(giveUp);
}

/* ---------- hero drift: crumbs and cocoa dust floating around the bar once the intro has settled ---------- */

const SPRITE = 128;

const makeCanvas = (size) => {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  return c;
};

// stand-in when the photo strip is missing: an irregular chip, dark cookie or white chocolate
function paintCrumb(choc) {
  const c = makeCanvas(SPRITE);
  const x = c.getContext('2d');
  const n = 7 + ((Math.random() * 5) | 0);
  const r = SPRITE * 0.36;
  const m = SPRITE / 2;
  x.beginPath();
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + Math.random() * 0.4;
    const k = r * (0.62 + Math.random() * 0.38);
    x.lineTo(m + Math.cos(a) * k, m + Math.sin(a) * k * (choc ? 0.55 : 0.85));
  }
  x.closePath();
  const g = x.createLinearGradient(m - r, m - r, m + r, m + r);
  g.addColorStop(0, choc ? '#fbf6e6' : '#4d3d32');
  g.addColorStop(1, choc ? '#a3946f' : '#100b08');
  x.fillStyle = g;
  x.fill();
  x.lineWidth = 2;
  x.strokeStyle = choc ? 'rgba(255,255,255,0.45)' : 'rgba(150,195,255,0.35)';
  x.stroke();
  return { src: c, sx: 0, size: SPRITE };
}

// photo strip: square cells side by side, drawn straight from the image (no copies)
function crumbCells(img) {
  const cells = [];
  if (img && img.naturalHeight) {
    const s = img.naturalHeight;
    for (let x = 0; x + s <= img.naturalWidth; x += s) cells.push({ src: img, sx: x, size: s });
  }
  if (!cells.length) for (let i = 0; i < 9; i++) cells.push(paintCrumb(i % 3 === 2));
  return cells;
}

function drift(canvas) {
  const hero = canvas.closest('.evo-rv-hero');
  if (!hero || !canvas.getContext || navigator.connection?.saveData) return;
  const media = hero.querySelector('.evo-rv-hero__media');
  const video = media && media.querySelector('video');
  const beamCanvas = hero.querySelector('canvas[data-rv-beam]');
  const ctx = canvas.getContext('2d');
  const ac = new AbortController();
  const on = { signal: ac.signal };
  const rand = (a, b) => a + Math.random() * (b - a);
  const smooth = (a, b, v) => {
    const t = clamp((v - a) / (b - a));
    return t * t * (3 - 2 * t);
  };
  // video time (s) at which the light sweep crosses the lettering: the crumbs burst out from behind the bar then
  const burstAt = parseFloat(canvas.dataset.burstAt) || 0;

  // photo crumbs, fetched right away at low priority and decoded before use; drawn stand-ins only if that fails
  let cells = null;
  let photos = false;
  if (canvas.dataset.sprites) {
    const img = new Image();
    img.fetchPriority = 'low';
    img.src = canvas.dataset.sprites;
    img.decode().then(
      () => {
        cells = crumbCells(img);
        photos = true;
        if (reduce.matches && settledAt) draw(0);
      },
      () => {}
    );
  }
  const getCells = () => cells || (cells = crumbCells());

  let W = 0;
  let H = 0;
  let dpr = 1;
  let top = 0;
  const bar = { x: 0, y: 0, rx: 1, ry: 1 };
  // where the video is drawn (object-fit cover inside its window), for the bar silhouette below
  const vid = { x: 0, y: 0, w: 0, h: 0 };
  // the headline block and the header band stay clear
  const copy = { l: 0, r: 0, b: 0 };
  const pill = { l: 0, r: 0, t: Infinity };
  const beam = { x: 0, y: 0, ux: 0, uy: 1, len: 1, half: 0.2 };

  // the bar sits in the middle of the video frame (78 % wide on desktop, 90 % on phones)
  const measure = () => {
    const r = hero.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = r.width;
    H = r.height;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    const frame = media && media.firstElementChild;
    const m = frame ? frame.getBoundingClientRect() : r;
    const mobile = phone.matches;
    // the video is drawn with object-fit cover into that window: 2:1 (bar 78 % wide, centred), phones 1:1
    // (bar 90 % wide at 45 %); the bar is about 0.29 as tall as it is wide
    const ar = mobile ? 1 : 2;
    const oy = mobile ? 0.45 : 0.5;
    const vw = Math.max(m.width, m.height * ar);
    const vh = vw / ar;
    const bw = vw * (mobile ? 0.9 : 0.78);
    bar.x = m.left - r.left + m.width / 2;
    bar.y = m.top - r.top + (m.height - vh) * oy + vh * oy;
    vid.x = m.left - r.left + (m.width - vw) / 2;
    vid.y = m.top - r.top + (m.height - vh) * oy;
    vid.w = vw;
    vid.h = vh;
    bar.rx = Math.max(1, (bw / 2) * 1.04);
    bar.ry = Math.max(1, ((bw * 0.29) / 2) * 1.12);
    const texts = [...hero.querySelectorAll('.evo-rv-hero__title > *, .evo-rv-hero__launch')].map((n) => n.getBoundingClientRect());
    if (texts.length) {
      copy.l = Math.min(...texts.map((b) => b.left)) - r.left - 40;
      copy.r = Math.max(...texts.map((b) => b.right)) - r.left + 40;
      copy.b = Math.max(...texts.map((b) => b.bottom)) - r.top + 36;
    }
    const pb = hero.querySelector('.evo-rv-hero__pill')?.getBoundingClientRect();
    if (pb && pb.width) {
      pill.l = pb.left - r.left;
      pill.r = pb.right - r.left;
      pill.t = pb.top - r.top;
    }
    top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    // light from far above the upper left corner onto the bar; it only becomes visible below the header
    beam.x = W * (mobile ? -0.05 : 0.1);
    beam.y = -H * (mobile ? 0.45 : 0.6);
    const dx = bar.x - beam.x;
    const dy = bar.y - beam.y;
    beam.len = Math.hypot(dx, dy) || 1;
    beam.ux = dx / beam.len;
    beam.uy = dy / beam.len;
    beam.half = (mobile ? 10 : 8) * (Math.PI / 180);
    scale = mobile ? 0.62 : 1;
    return true;
  };

  // 0..1: brightness of the cone at a point (soft across, fades in after the header and out past the bar)
  const inBeam = (x, y) => {
    const dx = x - beam.x;
    const dy = y - beam.y;
    const d = Math.hypot(dx, dy) || 1;
    const ang = Math.acos(clamp((dx * beam.ux + dy * beam.uy) / d, -1, 1)) / beam.half;
    if (ang > 2.2) return 0;
    const l = d / beam.len;
    return Math.exp(-ang * ang * 2.4) * smooth(0.42, 0.72, l) * (1 - smooth(0.92, 1.25, l)) * (1.15 - 0.3 * l);
  };

  // a soft gradient, so a small transparent canvas is enough (CSS scales it up); a little noise against banding.
  // Painted once the intro has settled and again only when the size really changed; the breathing is CSS.
  let beamKey = '';
  const paintBeam = () => {
    if (!beamCanvas || !W) return;
    const k = Math.min(0.35, 480 / W);
    const w = Math.max(1, Math.round(W * k));
    const h = Math.max(1, Math.round(H * k));
    const key = [w, h, bar.x | 0, bar.y | 0].join();
    if (key === beamKey) return;
    beamKey = key;
    beamCanvas.width = w;
    beamCanvas.height = h;
    const b = beamCanvas.getContext('2d');
    const img = b.createImageData(w, h);
    const px = img.data;
    for (let j = 0, i = 0; j < h; j++) {
      for (let x = 0; x < w; x++, i += 4) {
        const v = inBeam(x / k, j / k);
        if (v <= 0) continue;
        px[i] = 255;
        px[i + 1] = 245;
        px[i + 2] = 230;
        px[i + 3] = v * 28 + Math.random() - 0.5; // subtle: a stronger cone reads as a grey haze behind the bar
      }
    }
    b.putImageData(img, 0, 0);
  };
  const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 600 }) : setTimeout(fn, 60));

  // the bar's silhouette, taken from the intro's last frame: everything drawn over it is cut away, so the crumbs
  // are really behind the bar (also its crimped ends) and come out from behind it
  let mask = null;
  const buildMask = () => {
    const pic = media && media.querySelector('.evo-rv-hero__frame--end');
    if (!pic || mask) return;
    const url = (phone.matches && pic.querySelector('source')?.srcset) || pic.querySelector('img')?.src;
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.decode().then(
      () => {
        const w = 320;
        const h = Math.max(1, Math.round((w * img.naturalHeight) / img.naturalWidth));
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const x = c.getContext('2d', { willReadFrequently: true });
        x.drawImage(img, 0, 0, w, h);
        const d = x.getImageData(0, 0, w, h);
        const px = d.data;
        for (let i = 0; i < px.length; i += 4) {
          const a = Math.max(px[i], px[i + 1], px[i + 2]) > 9 ? 255 : 0;
          px[i] = px[i + 1] = px[i + 2] = 0;
          px[i + 3] = a;
        }
        x.putImageData(d, 0, 0);
        mask = c;
      },
      () => {}
    );
  };

  const crumbs = [];
  const motes = [];
  // phones: smaller crumbs, a near one must not cover half the screen
  let scale = 1;
  const crumb = (o) => ({ a: rand(0, 6.28), va: rand(-0.5, 0.5), c: Math.random(), ph: rand(0, 6.28), age: 9, drag: 0.8, ...o });

  // the burst: a fan from behind the bar, opening sideways, hard drag into a slow-motion hang; the same crumbs
  // then keep floating. A few big ones fly past the camera to the sides and are gone.
  const burst = () => {
    const mobile = phone.matches;
    for (let i = 0, n = mobile ? 9 : 14; i < n; i++) {
      const z = Math.pow(Math.random(), 1.3);
      // biased to the ends of the bar, so the gap under the headline stays calm
      const u = Math.sign(rand(-1, 1)) * Math.pow(Math.random(), 0.6);
      const ang = -Math.PI / 2 + u * 1.4 + rand(-0.25, 0.25);
      const v = rand(520, 1250) * (0.5 + 0.6 * z) * (mobile ? 0.75 : 1);
      crumbs.push(
        crumb({
          z,
          x: bar.x + u * bar.rx * 0.92,
          y: bar.y - bar.ry * rand(0.1, 0.8),
          vx: Math.cos(ang) * v,
          vy: Math.sin(ang) * v * 0.7,
          va: rand(-7, 7),
          s: (14 + 52 * z * z) * rand(0.85, 1.15) * scale,
          age: 0,
          drag: rand(3.8, 6),
        })
      );
    }
    crumbs.sort((p, q) => p.z - q.z);
  };

  // without a burst (reduced motion, no autoplay, scrolled away): they wait on a ring around the bar
  // free spots: on screen, below the header, off the headline block, the bar and the facts pill
  const clear = (x, y, m) => {
    if (x < m || x > W - m || y < top + m || y > H - m) return false;
    if (x > copy.l - m && x < copy.r + m && y < copy.b + m) return false;
    if (x > pill.l - m && x < pill.r + m && y > pill.t - m) return false;
    const dx = (x - bar.x) / (bar.rx + m);
    const dy = (y - bar.y) / (bar.ry + m);
    return dx * dx * dx * dx + dy * dy * dy * dy > 1.2;
  };
  const ring = () => {
    for (let i = 0, n = phone.matches ? 11 : 20, tries = 0; i < n && tries < 600; tries++) {
      const z = Math.pow(Math.random(), 1.4);
      const s = (14 + 52 * z * z) * rand(0.85, 1.15) * scale;
      const x = rand(0, W);
      const y = rand(top, H);
      // reduced motion shows this as a still, so the spots must be clean
      if (!clear(x, y, s / 2)) continue;
      // still drifting away from the bar a little, like the end of the burst
      const a = Math.atan2(y - bar.y, x - bar.x);
      const v = rand(10, 35) * (0.4 + z);
      crumbs.push(crumb({ z, x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, s, calm: true }));
      i++;
    }
    crumbs.sort((p, q) => p.z - q.z);
  };

  const spawnMotes = () => {
    for (let i = 0, n = phone.matches ? 45 : 90; i < n; i++) {
      motes.push({ x: rand(0, W), y: rand(0, H), z: rand(0.2, 1), ph: rand(0, 6.28), vx: rand(-5, 5), vy: rand(-7, 2) });
    }
  };

  const wrap = (p, m) => {
    if (p.x < -m) p.x += W + 2 * m;
    else if (p.x > W + m) p.x -= W + 2 * m;
    if (p.y < -m) p.y += H + 2 * m;
    else if (p.y > H + m) p.y -= H + 2 * m;
  };

  let px = 0;
  let py = 0;
  let tx = 0;
  let ty = 0;

  const step = (dt, t) => {
    for (let i = crumbs.length - 1; i >= 0; i--) {
      const p = crumbs[i];
      p.age += dt;
      const young = p.age < 0.6;
      if (!young) {
        // keep clear of the bar and of the headline block
        // the bar is a long rounded rectangle: a superellipse (power 4) hugs its corners
        const dx = (p.x - bar.x) / bar.rx;
        const dy = (p.y - bar.y) / bar.ry;
        const d = dx * dx * dx * dx + dy * dy * dy * dy;
        if (d < 1.6) {
          p.vx += dx * (1.6 - d) * 90 * dt;
          p.vy += dy * (1.6 - d) * 90 * dt;
        }
        if (p.x > copy.l && p.x < copy.r && p.y < copy.b) {
          const toL = p.x - copy.l;
          const toR = copy.r - p.x;
          const k = 140 * dt;
          if (copy.b - p.y < Math.min(toL, toR)) p.vy += k;
          else p.vx += toL < toR ? -k : k;
        }
      }
      // the burst drag relaxes into the calm float
      const drag = 0.8 + (p.drag - 0.8) * Math.exp(-p.age * 1.6);
      const f = Math.exp(-dt * drag);
      p.vx *= f;
      p.vy *= f;
      p.va *= Math.exp(-dt * (young ? 1.2 : 0.4));
      if (Math.abs(p.va) < 0.3) p.va = Math.sign(p.va || 1) * 0.3;
      const w = (4 + 9 * p.z) * smooth(0.4, 1.6, p.age);
      p.x += (p.vx + Math.cos(t * 0.21 + p.ph) * w) * dt;
      p.y += (p.vy + Math.sin(t * 0.17 + p.ph * 1.3) * w * 0.7 + (2.5 + 18 * Math.exp(-p.age)) * p.z) * dt;
      p.a += p.va * dt;
      if (p.once) {
        if (p.x < -p.s || p.x > W + p.s || p.y < -p.s || p.y > H + p.s) crumbs.splice(i, 1);
      } else wrap(p, p.s);
    }
    for (const m of motes) {
      m.x += (m.vx + Math.sin(t * 0.3 + m.ph) * 3) * dt;
      m.y += m.vy * dt;
      wrap(m, 4);
    }
    const e = Math.min(1, dt * 3);
    px += (tx - px) * e;
    py += (ty - py) * e;
  };

  let settledAt = 0;

  const draw = (t) => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const sy = Math.min(Math.max(window.scrollY, 0), H);
    const calm = settledAt ? (reduce.matches ? 1 : clamp((performance.now() - settledAt) / 1600)) : 0;
    if (calm > 0) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = '#fff3e0';
      for (const m of motes) {
        const y = m.y - sy * m.z * 0.25;
        const a = calm * (0.07 + 0.6 * inBeam(m.x, m.y)) * (0.6 + 0.4 * Math.sin(t * 1.7 + m.ph)) * smooth(top, top + 60, y);
        if (a < 0.01) continue;
        const r = (0.45 + m.z * 1.05) * dpr;
        ctx.globalAlpha = a;
        ctx.fillRect((m.x + px * m.z * 10) * dpr - r, (y + py * m.z * 10) * dpr - r, r * 2, r * 2);
      }
      ctx.globalCompositeOperation = 'source-over';
    }
    if (!crumbs.length) return;
    const list = getCells();
    for (const p of crumbs) {
      // until the silhouette is there: a crumb over the bar is simply hidden
      if (!mask) {
        const bx = (p.x - bar.x) / bar.rx;
        const by = (p.y - bar.y) / bar.ry;
        if (bx * bx * bx * bx + by * by * by * by < 1) continue;
      }
      const x = p.x + px * p.z * 24;
      const y = p.y - sy * (0.15 + p.z * 0.5) + py * p.z * 24;
      const a = (0.45 + 0.55 * Math.min(1, p.z * 1.6)) * smooth(top, top + 80, y) * (p.calm ? calm : 1);
      if (a < 0.01) continue;
      const cell = list[(p.c * list.length) | 0];
      const src = cell.src;
      const sx = cell.sx;
      const sw = cell.size;
      const s = p.s * dpr;
      const cos = Math.cos(p.a);
      const sin = Math.sin(p.a);
      // motion blur while they are fast: a few fading copies along the path
      const ghosts = Math.hypot(p.vx, p.vy) > 260 ? 3 : 0;
      for (let g = ghosts; g >= 0; g--) {
        const back = g * 0.011;
        ctx.globalAlpha = a * (g ? 0.42 / (g + 0.6) : 1);
        ctx.setTransform(cos, sin, -sin, cos, (x - p.vx * back) * dpr, (y - p.vy * back) * dpr);
        ctx.drawImage(src, sx, 0, sw, sw, -s / 2, -s / 2, s, s);
      }
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    if (mask && vid.w) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(mask, vid.x * dpr, vid.y * dpr, vid.w * dpr, vid.h * dpr);
      ctx.globalCompositeOperation = 'source-over';
    }
  };

  let running = false;
  let visible = true;
  let raf = 0;
  let last = 0;
  let t0 = 0;
  let odd = false;
  let burstDone = false;
  const frame = (now) => {
    raf = 0;
    if (!running || !visible || document.hidden || reduce.matches) return;
    raf = requestAnimationFrame(frame);
    // phones: 30 fps once the intro is over, the float is slow anyway
    odd = !odd;
    if (settledAt && odd && phone.matches) return;
    const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
    last = now;
    if (!burstDone && burstAt && video && video.currentTime >= burstAt) {
      burstDone = true;
      // only with the photo crumbs and only while the video is really at that moment
      if (photos && !video.ended && video.currentTime < burstAt + 0.5) burst();
    }
    step(dt, (now - t0) / 1000);
    draw((now - t0) / 1000);
  };
  const play = () => {
    if (!running || reduce.matches || raf || !visible || document.hidden) return;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  };
  const start = () => {
    if (running || !measure()) return;
    buildMask();
    running = true;
    t0 = performance.now();
    play();
  };

  // the intro has settled: light cone, dust, and crumbs on a ring if there was no burst
  const settle = () => {
    if (settledAt) return;
    if (!running && !measure()) return void setTimeout(settle, 250);
    burstDone = true;
    if (!t0) t0 = performance.now();
    settledAt = performance.now();
    buildMask();
    spawnMotes();
    if (!crumbs.length) ring();
    idle(paintBeam);
    hero.classList.add('is-settled');
    if (reduce.matches) return draw(0);
    running = true;
    play();
  };

  let mo = null;
  const settled = () => !media || !media.hasAttribute('data-rv-intro') || /^(done|end)$/.test(media.dataset.state);
  if (settled()) setTimeout(settle, 300);
  else {
    video?.addEventListener('playing', start, { once: true, signal: ac.signal });
    mo = new MutationObserver(() => {
      if (!settled()) return;
      mo.disconnect();
      settle();
    });
    mo.observe(media, { attributes: true, attributeFilter: ['data-state'] });
  }

  // resizes: debounced; the particles keep their relative place, then one redraw
  let lastW = 0;
  let lastH = 0;
  let roT = 0;
  const ro = window.ResizeObserver
    ? new ResizeObserver(() => {
        if (!running && !settledAt) return;
        clearTimeout(roT);
        roT = setTimeout(() => {
          const r = hero.getBoundingClientRect();
          if (!r.width || !r.height) return;
          if (lastW) {
            const kx = r.width / lastW;
            const ky = r.height / lastH;
            for (const p of crumbs) {
              p.x *= kx;
              p.y *= ky;
            }
            for (const m of motes) {
              m.x *= kx;
              m.y *= ky;
            }
          }
          lastW = r.width;
          lastH = r.height;
          if (!measure()) return;
          if (settledAt) paintBeam();
          draw((performance.now() - t0) / 1000);
        }, 150);
      })
    : null;
  if (ro) {
    ro.observe(hero);
    if (media) ro.observe(media);
    const r = hero.getBoundingClientRect();
    lastW = r.width;
    lastH = r.height;
  }
  const io = window.IntersectionObserver
    ? new IntersectionObserver((entries) => {
        visible = entries[entries.length - 1].isIntersecting;
        play();
      })
    : null;
  io?.observe(hero);
  document.addEventListener('visibilitychange', play, on);
  reduce.addEventListener?.(
    'change',
    () => {
      if (reduce.matches) {
        cancelAnimationFrame(raf);
        raf = 0;
        if (settledAt) draw(0);
      } else play();
    },
    on
  );
  if (window.matchMedia('(pointer: fine)').matches) {
    hero.addEventListener(
      'pointermove',
      (e) => {
        tx = (e.clientX / window.innerWidth) * 2 - 1;
        ty = (e.clientY / window.innerHeight) * 2 - 1;
      },
      on
    );
  }

  // theme editor: a reloaded section must not leave this loop and its listeners behind
  canvas._rvStop = () => {
    running = false;
    cancelAnimationFrame(raf);
    clearTimeout(roT);
    ro?.disconnect();
    io?.disconnect();
    mo?.disconnect();
    ac.abort();
  };
}

document.addEventListener('shopify:section:unload', (e) =>
  e.target.querySelectorAll('canvas[data-rv-drift]').forEach((c) => c._rvStop?.())
);

/* ---------- viewport progress, lit words ---------- */

function view(el) {
  let last = -1;
  addTask(el, (vh) => {
    const r = el.getBoundingClientRect();
    const v = reduce.matches ? 1 : clamp((vh - r.top) / (vh + r.height));
    if (Math.abs(v - last) < 0.0005) return;
    last = v;
    el.style.setProperty('--v', v.toFixed(4));
  });
}

function words(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  texts.forEach((t) => {
    if (!t.nodeValue.trim()) return;
    const frag = document.createDocumentFragment();
    t.nodeValue.split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        frag.appendChild(document.createTextNode(part));
      } else {
        const s = document.createElement('span');
        s.className = 'rv-w';
        s.textContent = part;
        frag.appendChild(s);
      }
    });
    t.parentNode.replaceChild(frag, t);
  });
  const list = [...el.querySelectorAll('.rv-w')];
  let lit = -1;
  addTask(el, (vh) => {
    const r = el.getBoundingClientRect();
    const prog = reduce.matches ? 1 : clamp((vh * 0.82 - r.top) / (r.height + vh * 0.3));
    const n = Math.round(prog * list.length);
    if (n === lit) return;
    const [from, to] = n > lit ? [Math.max(lit, 0), n] : [n, lit];
    for (let i = from; i < to && i < list.length; i++) list[i].classList.toggle('is-lit', i < n);
    lit = n;
  });
}

/* ---------- reveal + counters ---------- */

const revealIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.classList.add('is-in');
            revealIO.unobserve(e.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      )
    : null;

const fmt = (v, d) => v.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });

function countUp(el) {
  const to = parseFloat(String(el.dataset.rvCount).replace(',', '.')) || 0;
  const from = parseFloat(String(el.dataset.rvFrom || '0').replace(',', '.')) || 0;
  const dec = parseInt(el.dataset.rvDecimals || '0', 10);
  if (reduce.matches) {
    el.textContent = fmt(to, dec);
    return;
  }
  const t0 = performance.now();
  const dur = 1600;
  const step = (now) => {
    const t = clamp((now - t0) / dur);
    const e = 1 - Math.pow(2, -10 * t);
    el.textContent = fmt(from + (to - from) * (t >= 1 ? 1 : e), dec);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

const countIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            countIO.unobserve(e.target);
            countUp(e.target);
          });
        },
        { threshold: 0.6 }
      )
    : null;

/* ---------- videos play only while visible ---------- */

const videoIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            const v = e.target;
            if (e.isIntersecting && !reduce.matches) {
              const p = v.play();
              if (p && p.catch) p.catch(() => {});
            } else {
              v.pause();
            }
          });
        },
        { threshold: 0.35 }
      )
    : null;

const bgIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, io) =>
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.style.setProperty('--rv-img', e.target.dataset.rvBg);
            io.unobserve(e.target);
          }),
        { rootMargin: '150% 0px' }
      )
    : null;

/* ---------- highlights gallery ---------- */

class Gallery {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-rv-track]');
    this.cards = [...el.querySelectorAll('[data-rv-card]')];
    this.dots = [...el.querySelectorAll('[data-rv-dot]')];
    this.btn = el.querySelector('[data-rv-toggle]');
    this.ms = (parseFloat(el.dataset.rvInterval) || 6) * 1000;
    this.i = 0;
    this.t = 0;
    this.playing = !reduce.matches;
    this.visible = false;
    this.lockUntil = 0;
    this.loop = this.loop.bind(this);
    if (!this.track || !this.cards.length) return;

    this.dots.forEach((d, k) => d.addEventListener('click', () => this.go(k, true)));
    this.btn?.addEventListener('click', () => this.toggle());

    let timer = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.sync(), 90);
      },
      { passive: true }
    );
    const user = () => {
      if (performance.now() > this.lockUntil) this.pause();
    };
    this.track.addEventListener('pointerdown', user, { passive: true });
    this.track.addEventListener('wheel', (e) => Math.abs(e.deltaX) > Math.abs(e.deltaY) && user(), { passive: true });

    new IntersectionObserver(
      (entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
        if (this.visible) this.start();
        this.media();
      },
      { threshold: 0.4 }
    ).observe(el);

    this.setActive(0);
    this.state();
  }

  offset(k) {
    const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    return this.cards[k].offsetLeft - pad;
  }

  go(k, byUser) {
    const n = this.cards.length;
    k = ((k % n) + n) % n;
    this.lockUntil = performance.now() + 900;
    this.track.scrollTo({ left: this.offset(k), behavior: reduce.matches ? 'auto' : 'smooth' });
    this.setActive(k);
    if (byUser && !this.playing) this.state();
  }

  sync() {
    if (performance.now() < this.lockUntil) return;
    const x = this.track.scrollLeft;
    let best = 0;
    let dist = Infinity;
    this.cards.forEach((c, k) => {
      const d = Math.abs(this.offset(k) - x);
      if (d < dist) {
        dist = d;
        best = k;
      }
    });
    if (best !== this.i) this.setActive(best);
  }

  // card videos follow the gallery: only the active card plays, from the start, and the pause button stops it
  video(k) {
    return this.cards[k]?.querySelector('[data-rv-video] video') || null;
  }

  media() {
    this.cards.forEach((c, j) => {
      const v = this.video(j);
      if (!v) return;
      if (j === this.i && this.visible && this.playing && !reduce.matches) {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else v.pause();
    });
  }

  setActive(k) {
    const changed = k !== this.i;
    this.i = k;
    this.t = 0;
    this.cards.forEach((c, j) => c.classList.toggle('is-active', j === k));
    const v = this.video(k);
    if (v && changed) v.currentTime = 0;
    this.dots.forEach((d, j) => {
      d.classList.toggle('is-active', j === k);
      d.setAttribute('aria-current', j === k ? 'true' : 'false');
      d.style.setProperty('--f', '0');
    });
    if (this.ended) {
      this.ended = false;
      this.state();
    }
    this.media();
  }

  start() {
    if (this.playing && this.visible && !this.raf) {
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.loop);
    }
  }

  loop(now) {
    this.raf = 0;
    if (!this.playing || !this.visible) return;
    this.t += now - this.last;
    this.last = now;
    // a card with a clip lasts exactly as long as the clip, so it never jumps back to its first frame
    const v = this.video(this.i);
    const ms = v && v.duration > 0 ? v.duration * 1000 : this.ms;
    const dot = this.dots[this.i];
    if (dot) dot.style.setProperty('--f', clamp(this.t / ms).toFixed(4));
    if (this.t >= ms) {
      if (this.i >= this.cards.length - 1) {
        this.playing = false;
        this.ended = true;
        this.state();
        this.media();
        return;
      }
      this.go(this.i + 1);
    }
    this.raf = requestAnimationFrame(this.loop);
  }

  pause() {
    this.playing = false;
    this.state();
    this.media();
  }

  toggle() {
    if (this.ended) {
      this.ended = false;
      this.playing = true;
      this.go(0);
    } else {
      this.playing = !this.playing;
    }
    this.state();
    this.start();
    this.media();
  }

  state() {
    if (!this.btn) return;
    const s = this.ended ? 'replay' : this.playing ? 'pause' : 'play';
    this.btn.dataset.state = s;
    this.btn.setAttribute('aria-label', this.btn.dataset[`label${s[0].toUpperCase()}${s.slice(1)}`] || s);
  }
}

/* ---------- sideways track while pinned ---------- */

function paddles(el) {
  const row = el.querySelector('[data-rv-row]');
  const [prev, next] = el.querySelectorAll('[data-rv-paddle]');
  if (!row || !prev || !next) return;
  const step = () => {
    const card = row.querySelector('li');
    const gap = card ? parseFloat(getComputedStyle(card.parentElement).columnGap) || 0 : 0;
    return card ? card.offsetWidth + gap : row.clientWidth * 0.8;
  };
  [prev, next].forEach((b) =>
    b.addEventListener('click', () =>
      row.scrollBy({ left: step() * Number(b.dataset.rvPaddle), behavior: reduce.matches ? 'auto' : 'smooth' })
    )
  );
  const sync = () => {
    const max = row.scrollWidth - row.clientWidth;
    prev.disabled = row.scrollLeft <= 2;
    next.disabled = row.scrollLeft >= max - 2;
    prev.parentElement.toggleAttribute('data-fits', max <= 2);
  };
  row.addEventListener('scroll', sync, { passive: true });
  new ResizeObserver(sync).observe(row);
  sync();
}

/* ---------- countdown ---------- */

function countdown(el) {
  const target = parseInt(el.dataset.rvCountdown, 10);
  if (!target) return;
  const cells = {};
  el.querySelectorAll('[data-u]').forEach((c) => (cells[c.dataset.u] = c));
  const pad = (n) => String(n).padStart(2, '0');
  const tick = () => {
    if (!el.isConnected) return;
    const left = Math.max(0, Math.floor((target - Date.now()) / 1000));
    if (!left) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    if (cells.d) cells.d.textContent = pad(Math.floor(left / 86400));
    if (cells.h) cells.h.textContent = pad(Math.floor((left % 86400) / 3600));
    if (cells.m) cells.m.textContent = pad(Math.floor((left % 3600) / 60));
    if (cells.s) cells.s.textContent = pad(left % 60);
    setTimeout(tick, 1000 - (Date.now() % 1000));
  };
  tick();
}

/* ---------- launch switch: at launch_at the CTAs point to the shop (only with a buy link in the hero) ---------- */

function liveSwitch() {
  const src = document.querySelector('[data-rv-live-link]');
  const when = parseInt(document.querySelector('[data-rv-launch]')?.dataset.rvLaunch, 10);
  const link = src?.dataset.rvLiveLink;
  if (!link || !when || document.documentElement.classList.contains('evo-rv-live')) return;
  const go = () => {
    document.documentElement.classList.add('evo-rv-live');
    document.querySelectorAll('[data-rv-cta]').forEach((a) => {
      a.href = link;
      if (a.dataset.liveLabel) a.textContent = a.dataset.liveLabel;
    });
    document.querySelectorAll('[data-live-text]').forEach((el) => {
      if (el.dataset.liveText) el.textContent = el.dataset.liveText;
    });
    document.querySelectorAll('[data-rv-prelaunch]').forEach((el) => (el.hidden = true));
    document.querySelectorAll('[data-rv-postlaunch]').forEach((el) => (el.hidden = false));
  };
  const ms = when - Date.now();
  if (ms <= 0) go();
  // a page left open over the launch switches by itself (setTimeout tops out at ~24.8 days)
  else if (ms < 2147483647) setTimeout(go, ms);
}

/* ---------- launch bar ---------- */

function floatBar(el) {
  const hero = el.closest('[data-rv-scene]') || el.parentElement;
  // the hero has its own CTA pill: the bar only comes once that is gone, never two sets of buttons at once
  const pill = hero.querySelector('.evo-rv-hero__pill');
  // controls it must never cover (gallery dots, swipe arrows)
  const avoid = () => document.querySelectorAll('.evo-rv-hl__ctrl, .evo-rv-paddles');
  let shown = null;
  addTask(el, (vh) => {
    const end = (pill || hero).getBoundingClientRect().bottom;
    const notify = document.querySelector('[data-rv-notify-section]');
    const nearSignup = notify ? notify.getBoundingClientRect().top < vh * 0.85 : false;
    const zone = vh - (el.offsetHeight + 34);
    let blocked = false;
    avoid().forEach((c) => {
      const r = c.getBoundingClientRect();
      if (r.height && r.bottom > zone && r.top < vh) blocked = true;
    });
    const show = end < 0 && !nearSignup && !blocked;
    if (show !== shown) {
      shown = show;
      el.classList.toggle('is-visible', show);
      el.inert = !show;
    }
  });
  request();
}

/* ---------- signup (Klaviyo client API, same format as assets/evo-something-big.js) ---------- */

const KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions';
const KLAVIYO_REVISION = '2024-07-15';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function markDone(listId) {
  document.querySelectorAll('[data-rv-klaviyo]').forEach((w) => {
    if (!listId || w.dataset.listId === listId) w.dataset.state = 'done';
  });
}

async function subscribe(form) {
  const wrap = form.closest('[data-rv-klaviyo]');
  if (!wrap || wrap.dataset.state === 'busy') return;
  const d = wrap.dataset;
  const err = wrap.querySelector('[data-rv-error]');
  const input = form.querySelector('input[type="email"]');
  const email = (input?.value || '').trim();
  const fail = (msg) => {
    wrap.dataset.state = '';
    if (err) {
      err.textContent = msg;
      err.hidden = false;
    }
  };
  if (!EMAIL_RE.test(email)) {
    fail(d.msgInvalid || 'Bitte gib eine gültige E-Mail-Adresse ein.');
    input?.focus();
    return;
  }
  if (err) err.hidden = true;
  if (design) {
    markDone(d.listId);
    return;
  }
  if (!d.listId || !d.companyId) {
    console.warn('EVO Reveal: Klaviyo-Listen-ID fehlt in der Sektion "EVO Reveal Erinnerung".');
    fail(d.msgInactive || 'Die Anmeldung ist noch nicht aktiv.');
    return;
  }
  wrap.dataset.state = 'busy';
  try {
    const res = await fetch(`${KLAVIYO_URL}?company_id=${encodeURIComponent(d.companyId)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
        'content-type': 'application/vnd.api+json',
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: d.source || 'EVO Launch-Seite',
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: { list: { data: { type: 'list', id: d.listId } } },
        },
      }),
    });
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const j = await res.json();
        if (j?.errors?.[0]?.detail) detail = j.errors[0].detail;
      } catch (e) {
        /* body was not JSON */
      }
      throw new Error(detail);
    }
    try {
      localStorage.setItem(`evo-rv:${d.listId}`, '1');
    } catch (e) {
      /* private mode */
    }
    markDone(d.listId);
    window.dispatchEvent(new CustomEvent('evo-rv:subscribed', { detail: { listId: d.listId, source: d.source } }));
  } catch (e) {
    console.error('EVO Reveal, Klaviyo:', e.message);
    fail(d.msgError || 'Das hat gerade nicht geklappt. Bitte versuch es gleich noch einmal.');
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target.closest?.('form[data-rv-signup]');
  if (!form) return;
  e.preventDefault();
  subscribe(form);
});

// capture phase: the theme's anchor smooth-scroll must not jump the page as well
document.addEventListener(
  'click',
  (e) => {
    const link = e.target.closest?.('a[href$="#erinnere-mich"]');
    const sheet = document.querySelector('dialog[data-rv-sheet]');
    if (!link || !sheet || typeof sheet.showModal !== 'function') return;
    e.preventDefault();
    e.stopPropagation();
    if (!sheet.open) sheet.showModal();
    if (!phone.matches) sheet.querySelector('input[type="email"]')?.focus({ preventScroll: true });
  },
  true
);

function sheet(dlg) {
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg || e.target.closest('[data-rv-close]')) dlg.close();
  });
}

/* ---------- init ---------- */

function init(scope = document) {
  // the site-wide phase-2 loader would fetch all of this page's media right after load; here lazy stays lazy
  scope.querySelectorAll('.evo-rv').forEach((el) => el.setAttribute('data-evo-lazy', ''));
  scope.querySelectorAll('[data-rv-intro]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      intro(el);
    }
  });
  scope.querySelectorAll('canvas[data-rv-drift]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      drift(el);
    }
  });
  scope.querySelectorAll('[data-rv-scene]').forEach(scene);
  scope.querySelectorAll('[data-rv-view]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      view(el);
    }
  });
  scope.querySelectorAll('[data-rv-words]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      words(el);
    }
  });
  scope.querySelectorAll('[data-rv-reveal]').forEach((el) => (revealIO ? revealIO.observe(el) : el.classList.add('is-in')));
  scope.querySelectorAll('[data-rv-count]').forEach((el) => (countIO ? countIO.observe(el) : countUp(el)));
  scope.querySelectorAll('[data-rv-video] video').forEach((v) => {
    v.muted = true;
    // gallery videos are driven by the gallery
    if (!v.closest('[data-rv-gallery]')) videoIO?.observe(v);
  });
  scope.querySelectorAll('[data-rv-gallery]').forEach((el) => {
    if (!el._rv) el._rv = new Gallery(el);
  });
  // CSS background images (layers) are only set when their block comes within ~1.5 screens
  scope.querySelectorAll('[data-rv-bg]').forEach((el) => {
    if (el._rvBg) return;
    el._rvBg = true;
    const set = () => el.style.setProperty('--rv-img', el.dataset.rvBg);
    if (!bgIO) return set();
    bgIO.observe(el);
  });
  scope.querySelectorAll('[data-rv-paddles]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      paddles(el);
    }
  });
  scope.querySelectorAll('[data-rv-countdown]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      countdown(el);
    }
  });
  scope.querySelectorAll('[data-rv-float]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      floatBar(el);
    }
  });
  scope.querySelectorAll('dialog[data-rv-sheet]').forEach((el) => {
    if (!el._rv) {
      el._rv = true;
      sheet(el);
    }
  });
  liveSwitch();
  scope.querySelectorAll('[data-rv-klaviyo]').forEach((w) => {
    try {
      if (w.dataset.listId && localStorage.getItem(`evo-rv:${w.dataset.listId}`)) w.dataset.state = 'done';
    } catch (e) {
      /* private mode */
    }
  });
  request();
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
